import { useMemo, useState } from 'react';
import type { PublicForm } from '@cloudsign/shared';
import { useMutation } from '@tanstack/react-query';
import { Copy } from 'lucide-react';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { useListState } from '../../lib/useList';
import { ErrorPanel, errorMessage } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Alert, Badge, Button, ConfirmButton, FilterBar, PageHeader, Pager, SelectField, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { useActiveGroups, useAllSites } from '../preregistration/api';
import { useDeleteForm, useForms } from './api';
import { FormDialog } from './FormDialog';

const PAGE_SIZE = 20;

/** Copies text to the clipboard; false when the browser refuses. */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Public pre-registration forms: list, create and edit, copy the link, see how many people used each. */
export function FormsPage() {
  const toast = useToast();
  const canManage = useCan('forms.manage');
  const canDelete = useCan('forms.delete');
  const list = useListState();
  const forms = useForms();
  const sites = useAllSites();
  const groups = useActiveGroups();
  const del = useDeleteForm();
  const [siteId, setSiteId] = useState('');
  const [status, setStatus] = useState<'all' | 'on' | 'off'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'submissions'>('name');
  const [editing, setEditing] = useState<PublicForm | 'new' | null>(null);

  const siteList = sites.data?.items ?? [];
  const groupList = (groups.data ?? []).filter((g) => g.kind === 'standard');
  const siteName = (id: string) => siteList.find((s) => s.id === id)?.name ?? '';
  const groupName = (id: string) => (groups.data ?? []).find((g) => g.id === id)?.name ?? '';

  const filtered = useMemo(() => {
    const q = list.search.trim().toLowerCase();
    return (forms.data ?? [])
      .filter((f) => (siteId === '' || f.siteId === siteId) && (status === 'all' || (status === 'on') === f.enabled) && (q === '' || f.name.toLowerCase().includes(q) || f.slug.includes(q)))
      .sort((a, b) => (sortBy === 'submissions' ? b.submissions30d - a.submissions30d : a.name.localeCompare(b.name)));
  }, [forms.data, siteId, status, sortBy, list.search]);
  const pageItems = filtered.slice((list.page - 1) * PAGE_SIZE, list.page * PAGE_SIZE);

  const copy = useMutation({
    mutationFn: async (f: PublicForm) => {
      if (!(await copyText(f.publicUrl))) throw new Error('copy');
    },
    onSuccess: () => toast(t('w30.forms.copied')),
  });

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 sm:p-6">
      <PageHeader
        title={t('w30.forms.title')}
        description={t('w30.forms.description')}
        actions={
          canManage ? (
            <Button variant="primary" disabled={siteList.length === 0 || groupList.length === 0} onClick={() => setEditing('new')}>
              {t('w30.forms.new')}
            </Button>
          ) : null
        }
      />

      <FilterBar columns="sm:grid-cols-4">
        <TextField label={t('w30.forms.search')} type="search" value={list.search} onChange={(e) => list.setSearch(e.target.value)} />
        <SelectField
          label={t('w30.forms.siteFilter')}
          value={siteId}
          onChange={(e) => {
            setSiteId(e.target.value);
            list.resetPage();
          }}
        >
          <option value="">{t('w30.forms.allSites')}</option>
          {siteList.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </SelectField>
        <SelectField
          label={t('w30.forms.statusFilter')}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as typeof status);
            list.resetPage();
          }}
        >
          <option value="all">{t('w30.forms.status.all')}</option>
          <option value="on">{t('w30.forms.status.on')}</option>
          <option value="off">{t('w30.forms.status.off')}</option>
        </SelectField>
        <SelectField label={t('w30.forms.sort')} value={sortBy} onChange={(e) => setSortBy(e.target.value as typeof sortBy)}>
          <option value="name">{t('w30.forms.sort.name')}</option>
          <option value="submissions">{t('w30.forms.sort.submissions')}</option>
        </SelectField>
      </FilterBar>

      {copy.error ? <Alert>{t('w30.forms.copyFailed')}</Alert> : null}
      {del.error ? <Alert>{errorMessage(del.error)}</Alert> : null}

      {forms.isPending ? (
        <Spinner />
      ) : forms.error ? (
        <ErrorPanel error={forms.error} onRetry={() => void forms.refetch()} />
      ) : (
        <>
          <TableWrap caption={t('w30.forms.table')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <Th>{t('w30.forms.col.name')}</Th>
                <Th>{t('w30.forms.col.site')}</Th>
                <Th>{t('w30.forms.col.status')}</Th>
                <Th>{t('w30.forms.col.submissions')}</Th>
                <Th>{t('w30.forms.col.actions')}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pageItems.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-600">
                    {t('w30.forms.empty')}
                  </td>
                </tr>
              ) : (
                pageItems.map((f) => (
                  <tr key={f.id}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{f.name}</div>
                      <a href={f.publicUrl} target="_blank" rel="noreferrer" className="break-all text-xs text-brand underline">
                        {f.publicUrl}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {siteName(f.siteId)}
                      <div className="text-xs text-slate-600">{groupName(f.groupId)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        <Badge tone={f.enabled ? 'good' : 'neutral'}>{f.enabled ? t('w30.forms.badge.on') : t('w30.forms.badge.off')}</Badge>
                        {f.requireApproval ? <Badge tone="warn">{t('w30.forms.badge.approval')}</Badge> : null}
                      </div>
                    </td>
                    <td className="px-4 py-3 tabular-nums">{f.submissions30d}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <Button aria-label={t('w30.forms.copyLinkFor', { name: f.name })} onClick={() => copy.mutate(f)}>
                          <Copy aria-hidden="true" className="h-4 w-4" />
                          {t('w30.forms.copyLink')}
                        </Button>
                        {canManage ? (
                          <Button aria-label={t('w30.forms.editFor', { name: f.name })} onClick={() => setEditing(f)}>
                            {t('w30.forms.edit')}
                          </Button>
                        ) : null}
                        {canDelete ? (
                          <ConfirmButton
                            label={t('w30.forms.delete')}
                            pending={del.isPending}
                            onConfirm={() => del.mutate(f.id, { onSuccess: () => toast(t('w30.forms.deleted')) })}
                          />
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </TableWrap>
          <Pager page={list.page} pageSize={PAGE_SIZE} total={filtered.length} onPage={list.setPage} />
        </>
      )}

      {editing ? <FormDialog form={editing === 'new' ? null : editing} sites={siteList} groups={groupList} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}
